import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "src/redux/hooks";

interface Props {
  id: string;
  tag?: string;
  setValue: (id: string, value: any) => void;
}

/**
 * Custom hook to calculate DPF hours per cycle based on previous entry data.
 * Formula: (running-hours - ss-running-hours) / (dpf-running-cycles - ss-dpf-cycles)
 */
const useDPFHoursCalculation = ({ id, tag, setValue }: Props) => {
  const { getValues, watch } = useFormContext();
  const { lbentry, features } = useSelector(state => state.generatorSlice);

  const findFieldsInSameColumn = useCallback(
    (currentFieldId: string) => {
      const currentValues = getValues();
      const sections = lbentry?.sections || [];

      let currentColumnPosition = -1;

      sections.forEach(section => {
        section.rows?.forEach(row => {
          if (row.cols) {
            const colEntries = Object.entries(row.cols);
            colEntries.forEach(([fieldId], colIndex) => {
              if (fieldId === currentFieldId) {
                currentColumnPosition = colIndex;
              }
            });
          }
        });
      });

      if (currentColumnPosition === -1)
        return { fieldsInColumn: null, currentColumnPosition: -1 };

      const fieldsInColumn = {
        engine: null as any,
        runningHours: null as any,
        dpfCycles: null as any,
      };

      sections.forEach(section => {
        section.rows?.forEach(row => {
          if (row.cols) {
            const colEntries = Object.entries(row.cols);
            const fieldAtPosition = colEntries[currentColumnPosition];
            if (fieldAtPosition) {
              const [fieldId, col] = fieldAtPosition;
              const fieldTag = (col as any).tag;

              if (fieldTag === "ss-engine") {
                fieldsInColumn.engine = {
                  key: fieldId,
                  value: currentValues[fieldId],
                };
              } else if (fieldTag === "ss-running-hours") {
                fieldsInColumn.runningHours = {
                  key: fieldId,
                  value: currentValues[fieldId],
                };
              } else if (fieldTag === "ss-dpf-cycles") {
                fieldsInColumn.dpfCycles = {
                  key: fieldId,
                  value: currentValues[fieldId],
                };
              }
            }
          }
        });
      });
      return { fieldsInColumn, currentColumnPosition };
    },
    [getValues, lbentry?.sections],
  );

  const setEngineHeader = useCallback(() => {
    const fieldsInColumn = findFieldsInSameColumn(id)?.fieldsInColumn;

    if (fieldsInColumn?.engine) {
      const engineSelection = fieldsInColumn.engine.value;
      if (!engineSelection) return;
      const engineLabel = engineSelection.value || "";
      const currentColumnPosition =
        findFieldsInSameColumn(id)?.currentColumnPosition;
      if (currentColumnPosition === -1) return;
      const el =
        document.querySelectorAll(".ss-engine-header")?.[
          currentColumnPosition - 1
        ];
      if (el) {
        el.textContent = engineLabel;
      }
    }
  }, [id, findFieldsInSameColumn]);

  useEffect(() => {
    if (tag === "ss-engine") {
      setEngineHeader();
    }
  }, [tag, setEngineHeader]);

  const calculateDPFHours = useCallback(() => {
    if (tag !== "ss-dpf-hours") return;

    const ssValues =
      (lbentry as any)?.ssValues ||
      features?.ssValues ||
      (window as any)?.ssValues;

    if (!ssValues || typeof ssValues !== "object") {
      return;
    }

    const fieldsInColumn = findFieldsInSameColumn(id)?.fieldsInColumn;

    if (
      !fieldsInColumn?.engine ||
      !fieldsInColumn?.runningHours ||
      !fieldsInColumn?.dpfCycles
    ) {
      return;
    }

    // Check if engine is selected in this column
    const engineSelection = fieldsInColumn.engine.value;
    if (!engineSelection?.key) {
      return;
    }

    const engineIndex = engineSelection.key;

    // Get the corresponding previous entry data using the engine index
    let matchingPreviousEntry = Object.values(ssValues).find(
      (v: any) => v?.["ss-engine"]?.key === engineIndex,
    ) as Record<string, any> | undefined;

    if (!matchingPreviousEntry) {
      matchingPreviousEntry = {
        "ss-engine": { key: engineIndex },
        "ss-running-hours": 0,
        "ss-dpf-cycles": 0,
      };
    }

    // Get current values from this specific column
    const currentRunningHours =
      parseFloat(fieldsInColumn.runningHours.value) || 0;
    const currentDPFCycles = parseFloat(fieldsInColumn.dpfCycles.value) || 0;

    if (
      !fieldsInColumn.runningHours.value ||
      !fieldsInColumn.dpfCycles.value ||
      currentRunningHours === 0 ||
      currentDPFCycles === 0
    ) {
      return;
    }

    // Get previous values from matching entry
    const prevRunningHours = parseFloat(
      matchingPreviousEntry["ss-running-hours"] || 0,
    );
    const prevDPFCycles = parseFloat(
      matchingPreviousEntry["ss-dpf-cycles"] || 0,
    );

    // Calculate hours per cycle
    const runningHoursDiff = currentRunningHours - prevRunningHours;
    const dpfCyclesDiff = currentDPFCycles - prevDPFCycles;

    if (dpfCyclesDiff === 0) {
      return;
    }

    const hoursPerCycle = runningHoursDiff / dpfCyclesDiff;

    if (hoursPerCycle >= 0 && hoursPerCycle < 10000) {
      const calculatedValue = hoursPerCycle.toFixed(2);
      setValue(id, calculatedValue);
    }
  }, [id, tag, lbentry, features, setValue, findFieldsInSameColumn]);

  useEffect(() => {
    if (tag === "ss-dpf-hours") {
      const timer = setTimeout(() => {
        calculateDPFHours();
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [tag, calculateDPFHours]);

  useEffect(() => {
    if (tag === "ss-dpf-hours") {
      const fieldsInColumn = findFieldsInSameColumn(id)?.fieldsInColumn;

      if (fieldsInColumn) {
        const fieldsToWatch = [
          fieldsInColumn.engine?.key,
          fieldsInColumn.runningHours?.key,
          fieldsInColumn.dpfCycles?.key,
        ].filter(Boolean);

        if (fieldsToWatch.length > 0) {
          const subscription = watch((values, { name }) => {
            if (name && fieldsToWatch.includes(name)) {
              const timer = setTimeout(() => {
                calculateDPFHours();
              }, 150);
              return () => clearTimeout(timer);
            }
          });

          return subscription.unsubscribe;
        }
      }
    }
  }, [tag, id, watch, calculateDPFHours, findFieldsInSameColumn]);

  return { calculateDPFHours, setEngineHeader };
};

export default useDPFHoursCalculation;
